import { create } from 'zustand';
import errorHandler from '../utils/error-handler.util';
import {
  IBusinessAddressFormProps,
  IBusinessInfoFormProps,
  IdentityTypeEnum,
  IOnboarding,
  IOnboardingQuestionaire,
  IUltimateBeneficiary,
} from '../models/onboarding.model';
import { IApiResponse } from '../models/resource.model';
import moment from 'moment';
import createAxiosInstance from '../services/global/axiosInstance';
import { IUploadedDocument } from '../models/document.model';
import useRateStore from './rate';

const axiosInstance = createAxiosInstance();

interface IOnboardingStore {
  kybStatus: number | null;
  kybStatusDesc: string | null;
  loading: boolean;
  onboardingStage: number;
  businessInfo: IBusinessInfoFormProps;
  setBusinessInfo: (info: IBusinessInfoFormProps) => void;
  setBusinessType: (type: string) => void;
  updateBusinessInfo: () => Promise<IApiResponse | void>;
  businessAddress: IBusinessAddressFormProps;
  setBusinessAddress: (address: IBusinessAddressFormProps) => void;
  updateBusinessAddress: () => Promise<IApiResponse | void>;
  singleUltimateBeneficiary: IUltimateBeneficiary;
  businessUltimateBeneficiaries: IUltimateBeneficiary[];
  setUltimateBeneficiaryByIndex: (
    ben: IUltimateBeneficiary,
    index: number,
  ) => void;
  setAllUltimateBeneficiaries: (ben: IUltimateBeneficiary[]) => void;
  setUboCreateUpdatePayload: (uboIndex: number) => any;
  createUbo: (uboIndex: number) => Promise<IApiResponse | void>;
  updateUbo: (uboIndex: number) => Promise<IApiResponse | void>;
  deleteUbo: (uboId: string) => Promise<IApiResponse | void>;
  businessDocuments: IUploadedDocument[];
  setBusinessDocuments: (docs: IUploadedDocument[]) => void;
  updateBusinessDocuments: () => Promise<IApiResponse | void>;
  businessQuestionnaire: IOnboardingQuestionaire;
  setBusinessQuestionnaire: (questionaire: IOnboardingQuestionaire) => void;
  updateBusinessQuestioniare: () => Promise<IApiResponse | void>;
  onboardUser: (payload: IOnboarding) => Promise<IApiResponse>;
  getKybStatus: () => Promise<IApiResponse>;
}

const useOnboardingStore = create<IOnboardingStore>((set, get) => ({
  kybStatus: null,
  kybStatusDesc: null,
  loading: false,
  onboardingStage: 1,
  businessInfo: {
    businessType: '',
    email: '',
    description: '',
    website: '',
    registrationNumber: '',
  },
  setBusinessInfo: (info: IBusinessInfoFormProps) => {
    set({ businessInfo: { ...info } });
  },
  setBusinessType: (type: string) => {
    set({ businessInfo: { ...get().businessInfo, businessType: type } });
  },
  businessAddress: {
    streetLine1: '',
    streetLine2: '',
    city: '',
    state: '',
    postalCode: '',
    countryCode: '',
    countryName: '',
    countryId: '',
  },
  setBusinessAddress: (address: IBusinessAddressFormProps) => {
    set({ businessAddress: { ...address } });
  },
  updateBusinessAddress: async () => {
    set({ loading: true });
    try {
      const response = await axiosInstance.put(
        '/corporate/update-business-address',
        { address: { ...get().businessAddress } },
      );
      set({ loading: false });
      return response?.data;
    } catch (err: any) {
      errorHandler(err);
      set({ loading: false });
    }
  },
  updateBusinessInfo: async () => {
    set({ loading: true });
    try {
      const { businessType, email, description, website, registrationNumber } =
        get().businessInfo;
      const { countryName, countryId, ...rest } = get().businessAddress;
      const payload = {
        businessType,
        email,
        description,
        website,
        registrationNumber,
        address: { ...rest },
        countryId,
      };

      const response = await axiosInstance.put(
        '/corporate/update-business-detail',
        { ...payload },
      );
      set({ loading: false });
      return response?.data;
    } catch (err: any) {
      errorHandler(err);
      set({ loading: false });
    }
  },
  singleUltimateBeneficiary: {
    id: null,
    identityType: IdentityTypeEnum.Id_Number,
    open: false,
    firstName: '',
    lastName: '',
    birthDate: null,
    email: '',
    phone: '',
    streetLine1: '',
    streetLine2: '',
    city: '',
    state: '',
    stateTouched: false,
    postalCode: '',
    countryCode: '',
    countryName: '',
    countryCodeTouched: false,
    countryId: '',
    identityDocumentFrontId: null,
    identityDocumentFrontBase64File: '',
    identityDocumentFrontFileName: '',
    identityDocumentFrontFileType: '',
    identityDocumentBackId: null,
    identityDocumentBackBase64File: '',
    identityDocumentBackFileName: '',
    identityDocumentBackFileType: '',
    idIssuingCountryCode: '',
    idIssuedCountryId: '',
    idIssuingCountryCodeTouched: false,
    identityNumberType: '',
    identityNumberTypeTouched: false,
    identityNumber: '',
    identityDocumentType: '',
    identityDocumentTypeTouched: false,
  },
  businessUltimateBeneficiaries: [],
  setUltimateBeneficiaryByIndex: (ben: IUltimateBeneficiary, index: number) => {
    set((state) => {
      const updated = [...state.businessUltimateBeneficiaries];
      updated[index] = ben;
      return { businessUltimateBeneficiaries: updated };
    });
  },
  setAllUltimateBeneficiaries: (bens: IUltimateBeneficiary[]) => {
    set({ businessUltimateBeneficiaries: [...bens] });
  },
  businessDocuments: [],
  setUboCreateUpdatePayload: (uboIndex: number) => {
    const rawUBOs = get().businessUltimateBeneficiaries;
    const rawUbo = rawUBOs[uboIndex];

    if (!rawUbo) return null; // handle invalid index

    const { activeCountries: countries } = useRateStore.getState();

    const uboCountry = countries.find(
      (item) => item.iso2 === rawUbo.countryCode,
    );

    const transformedUbo: any = {
      ...(rawUbo.id && {
        id: rawUbo.id,
      }),
      firstName: rawUbo.firstName,
      lastName: rawUbo.lastName,
      email: rawUbo.email,
      phone: rawUbo.phone,
      identityType: rawUbo.identityType,
      birthDate: moment(rawUbo.birthDate).format('YYYY-MM-DD'),
      ...(rawUbo.identityType === IdentityTypeEnum.Id_Number && {
        identityDocumentFrontId: null,
        identityDocumentBackId: null,
      }),
      address: {
        streetLine1: rawUbo.streetLine1,
        streetLine2: rawUbo.streetLine2,
        city: rawUbo.city,
        state: rawUbo.state,
        postalCode: rawUbo.postalCode,
        countryCode: uboCountry ? uboCountry.iso3 : rawUbo.countryCode,
      },
      countryId: rawUbo.countryId ?? null,
      identityDocumentFrontId: rawUbo.identityDocumentFrontId ?? null,
      identityDocumentBackId: rawUbo.identityDocumentBackId ?? null,
      identity: {
        ...(rawUbo.identityType === IdentityTypeEnum.Id_Document && {
          identityNumber: '',
          identityNumberType: '',
          identityDocumentType: rawUbo.identityDocumentType,
        }),
        ...(rawUbo.identityType === IdentityTypeEnum.Id_Number && {
          identityDocumentType: '',
          identityNumberType: rawUbo.identityNumberType,
          identityNumber: rawUbo.identityNumber,
        }),
      },
      idIssuedCountryId: rawUbo.idIssuedCountryId ?? null,
    };

    return { ...transformedUbo };
  },
  createUbo: async (uboIndex: number) => {
    set({ loading: true });

    try {
      const response = await axiosInstance.post(
        '/corporate/create-business-ubo',
        { ...get().setUboCreateUpdatePayload(uboIndex) },
      );

      set({ loading: false });
      return response?.data;
    } catch (err: any) {
      errorHandler(err);
      set({ loading: false });
    }
  },
  updateUbo: async (uboIndex: number) => {
    set({ loading: true });

    try {
      const response = await axiosInstance.put(
        '/corporate/update-business-ubo',
        { ...get().setUboCreateUpdatePayload(uboIndex) },
      );

      set({ loading: false });
      return response?.data;
    } catch (err: any) {
      // errorHandler(err, toast.error);
      set({ loading: false });
    }
  },
  deleteUbo: async (uboId: string) => {
    set({ loading: true });
    try {
      const response = await axiosInstance.delete(
        `/corporate/business/ubo/${uboId}`,
      );
      set({ loading: false });
      return response?.data;
    } catch (err: any) {
      // errorHandler(err, toast.error);
      set({ loading: false });
    }
  },
  setBusinessDocuments: (docs: IUploadedDocument[]) => {
    set({ businessDocuments: docs });
  },
  updateBusinessDocuments: async () => {
    set({ loading: true });
    try {
      const response = await axiosInstance.put(
        '/corporate/update-business-documents',
        {
          businessDocuments: get()
            .businessDocuments.map((doc) => doc.docId)
            .filter((id): id is string => id !== null),
        },
      );
      set({ loading: false });
      return response?.data;
    } catch (err: any) {
      errorHandler(err);
      set({ loading: false });
    }
  },
  businessQuestionnaire: {
    sourceOfFund: '',
    transmitsCustomerFunds: false,
    estimatedAnnualRevenueUsd: '',
    estimatedMonthlyPayOut: 0,
    sourceOfFundDescription: '',
  },
  setBusinessQuestionnaire: (questionaire: IOnboardingQuestionaire) => {
    set({ businessQuestionnaire: { ...questionaire } });
  },
  updateBusinessQuestioniare: async () => {
    set({ loading: true });
    try {
      const questionaire = get().businessQuestionnaire;
      const response = await axiosInstance.put(
        '/corporate/update-business-questioniare',
        {
          questionnaire: {
            ...questionaire,
            transmitsCustomerFunds:
              questionaire.transmitsCustomerFunds === 'Yes',
            estimatedMonthlyPayOut:
              questionaire.estimatedMonthlyPayOut === 'Less than $10,000'
                ? 10000
                : questionaire.estimatedMonthlyPayOut === '$10,000 - $50,000'
                  ? 50000
                  : questionaire.estimatedMonthlyPayOut === '$50,001 - $100,000'
                    ? 100000
                    : questionaire.estimatedMonthlyPayOut ===
                        '$100,001 - $500,000'
                      ? 500000
                      : 1000000,
          },
        },
      );
      set({ loading: false });
      return response?.data;
    } catch (err: any) {
      errorHandler(err);
      set({ loading: false });
    }
  },
  onboardUser: async (payload: IOnboarding) => {
    set({ loading: true });
    try {
      const response = await axiosInstance.post('/corporate/create', payload);
      set({ loading: false });
      return response?.data;
    } catch (err: any) {
      errorHandler(err);
      set({ loading: false });
    }
  },
  getKybStatus: async () => {
    try {
      const response = await axiosInstance.get('/corporate/kybstatus');

      const kybResp = response?.data.value.value[0];
      set({
        kybStatus: kybResp.verificationStatus,
        kybStatusDesc: kybResp.status,
        onboardingStage: kybResp.onboardingStages.length + 1,
      });
      return response?.data.value;
    } catch (err: any) {
      errorHandler(err);
    }
  },
}));

export default useOnboardingStore;
